import { defineStore } from 'pinia';
import { apolloClient } from '@/utils/apollo';
import constants from '@/utils/constants';
import { cloneDeep, get, uniqBy } from 'lodash';
import { activityRangeQuery } from '@/graphql/queries/activityRange.js';
import { useInstitutionStore } from '@/stores/institution';
import { useDepartmentStore } from '@/stores/department';
import { useProfilesStore } from '@/stores/profiles';
import { getUniqueInstitutions } from '@/utils/helpers';
import {
  IDepartment,
  IDocument,
  IImage,
  IInstitution,
  IPictogram,
  IProfile,
  IVideo,
} from '@/interfaces';
import api from '@/api';
import { ActivitySubscription, ScreenProfile } from '@/graphql/generated-types';

export interface IActivity {
  id: string;
  title: string;
  description: string;
  startDate: string;
  category?: string;
  endDate?: string;
  coverImage: {
    source: string;
  };
  participants: IProfile[];
  signupPossible: boolean;
  signupEnd: string;
  cancelled: boolean;
  pictures?: IImage[];
  departments?: IDepartment[];
  documents?: IDocument[];
  allCopy?: IActivity[];
  signupEnabled?: boolean;
  cancelEnabled?: boolean;
  maxSignups?: number;
  external?: boolean;
  responsible?: IProfile[];
  institution?: IInstitution;
  videos?: IVideo[];
  pictograms?: IPictogram[];
  club?: { name: string; logo: IImage } | null;
  price?: { price: number; currency: string }[];
}

const DEFAULT_ACTIVITY: IActivity = {
  id: '',
  title: '',
  description: '',
  startDate: '',
  endDate: '',
  coverImage: {
    source: '',
  },
  participants: [],
  signupPossible: false,
  signupEnd: '',
  cancelled: false,
  institution: {
    id: '',
    name: '',
    departments: [],
    externalConnections: [],
  },
  videos: [],
  pictograms: [],
  documents: [],
  allCopy: [],
  signupEnabled: false,
  cancelEnabled: false,
  maxSignups: 0,
  external: false,
  responsible: [],
  price: [{ price: 0, currency: 'DKK' }],
};

export interface IActivityState {
  all: IActivity[];
  external: IActivity[];
  selected: IActivity;
  uniqueInstitutions: IInstitution[] | null;
  allActivities: IActivity[];
  allCopy: IActivity[];
}

export const useActivitiesStore = defineStore('activities', {
  state: (): IActivityState => ({
    all: [],
    external: [],
    selected: DEFAULT_ACTIVITY,
    uniqueInstitutions: null,
    allActivities: [],
    allCopy: [],
  }),
  actions: {
    async getActivities() {
      const departmentStore = useDepartmentStore();

      if (!departmentStore.id) {
        throw new Error('No department id provided');
      }

      const dateNow = new Date();
      const sixMonthsAheadDays = dateNow.setDate(dateNow.getDate() + 185);
      const dateEnd = new Date(sixMonthsAheadDays).toISOString().split('T')[0];

      await apolloClient
        .query({
          query: activityRangeQuery,
          variables: {
            filter: {
              departments: departmentStore.id,
            },
            dateFrom: new Date().toISOString().split('T')[0],
            dateTo: dateEnd,
            timezone: 'Europe/Copenhagen',
          },
          fetchPolicy: 'no-cache',
        })
        .then((response) => {
          const institutionStore = useInstitutionStore();
          const allActivities = response.data.activityRange;

          this.all = allActivities;

          const externalConnections =
            institutionStore.externalConnections || [];
          const hasExternalActivityModule = (
            departmentStore.settings?.screenConfig?.footerConfig ?? []
          ).some((item) => item === 'EXTERNAL_ACTIVITIES');

          if (externalConnections.length && hasExternalActivityModule) {
            const externalActivityInstitutionId = get(
              externalConnections,
              '[0.institution.id',
              '-1'
            );

            const externalActivities = allActivities.filter(
              (activity: IActivity) =>
                activity.institution?.id === externalActivityInstitutionId
            );

            this.external = externalActivities;

            const internalActivities = allActivities.filter(
              (activity: IActivity) =>
                activity.institution?.id !== externalActivityInstitutionId
            );

            this.all = internalActivities;
          }

          this.allCopy = cloneDeep(this.all);
          this.uniqueInstitutions = getUniqueInstitutions(this.all);
        })
        .catch((error) => {
          console.log('Error getting activities', error);
        });
    },
    async getSelectedActivity({ activityId }: { activityId: string }) {
      const activityEntry = await api.activities.getActivityEntry(activityId);

      this.selected = {
        ...activityEntry,
        participants: (activityEntry?.participants || []).map((participant) => {
          if (!participant.showImage)
            participant.displayImage = constants.avatarPlaceholder;
          return participant;
        }),
        pictures: uniqBy(
          (activityEntry.pictures || []).concat(
            activityEntry.coverImage ? [activityEntry.coverImage] : []
          ),
          'id'
        ),
      } as unknown as IActivity;
    },
    async activitySignupScreen(
      participating: boolean,
      subscription: ActivitySubscription,
      profile: ScreenProfile
    ) {
      const profilesStore = useProfilesStore();

      const mutation = participating
        ? api.activities.signup
        : api.activities.cancelSignup;

      await mutation(profile, subscription)
        .then(async () => {
          profilesStore.setPinValidation({
            validating: false,
            validPin: true,
          });

          await this.getSelectedActivity({ activityId: subscription.id });
        })
        .catch((error) => {
          profilesStore.setPinValidation({
            validating: false,
            validPin: false,
          });
          throw new Error('Error signing up to activity', error);
        });
    },
  },
});
